import { ActiveTag, ChatTimelineThinkingMode, Tag } from "../types";
import {
  getEnhancedPreview,
  getProgressStage,
  getProgressStepId,
  parseProgressContent,
  renderMarkdownContent
} from "./utils";
import React from "react";
import ResearchPills from "./ResearchPills";
import { useSelector } from "react-redux";

interface ActiveStepCardProps {
  activeStep: ActiveTag | null;
  currentStep: ActiveTag | null;
  actualTags: Tag[];
  progressEndState: Record<string, boolean>;
  toggleProcessExpansion: () => void;
}

export const ActiveStepCard: React.FC<ActiveStepCardProps> = ({
  activeStep,
  currentStep,
  actualTags,
  progressEndState,
  toggleProcessExpansion,
}) => {
  const {
    thinkingMode
  } = useSelector((state: any) => state.chatMessages);

  if (!activeStep) return null;

  const enhancedPreview = getEnhancedPreview(activeStep);
  const isCurrentlyStreaming = currentStep && activeStep === currentStep;

  if (activeStep?.type === 'progress') {
    const stepId = getProgressStepId(activeStep?.content);
    const stepTags = actualTags.filter(
      (t) => t.type === 'progress' && getProgressStepId(t.content) === stepId
    );

    const startTag = stepTags.find((t) => getProgressStage(t.content) === 'start');
    const endTag = stepTags.find((t) => getProgressStage(t.content) === 'end');

    let messageToShow = null;
    if (startTag && endTag) {
      messageToShow = progressEndState[stepId]
        ? parseProgressContent(endTag.content)
        : parseProgressContent(startTag.content);
    } else if (endTag) {
      messageToShow = parseProgressContent(endTag.content);
    } else if (startTag) {
      messageToShow = parseProgressContent(startTag.content);
    }

    return (
      <div
        key={`active-step-${activeStep?.type}`}
        className="step-card single-card current-step"
        data-step-type={activeStep?.type}
        onClick={toggleProcessExpansion}
      >
        <div className="step-card-content">
          <div className="progress-content">
            <div className="markdown-content">
              <p>{messageToShow}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show pills for research in the collapsed active step view
  if (activeStep?.type === 'research') {
    return (
      <div
        key={`active-step-${activeStep?.type}`}
        className="step-card single-card current-step"
        data-step-type={activeStep?.type}
        onClick={toggleProcessExpansion}
      >
        <div className={`step-card-content ${isCurrentlyStreaming ? 'streaming' : ''}`}>
          <div className="research-content">
            <ResearchPills tag={activeStep} />
          </div>
        </div>
      </div>
    );
  }

  if (activeStep?.type === 'thinking') {
    const contentWithoutFirstBold = (activeStep?.content || '').replace(/\*\*[\s\S]*?\*\*/, '').trimStart();
    return (
      <div
        key={`active-step-${activeStep?.type}`}
        className="step-card single-card current-step"
        data-step-type={activeStep?.type}
        onClick={toggleProcessExpansion}
      >
        <div className={`step-card-content ${isCurrentlyStreaming ? 'streaming' : ''}`}>
          <div className="thinking-content">
            {renderMarkdownContent(contentWithoutFirstBold, false)}
          </div>
        </div>
      </div>
    );
  }

  if (activeStep?.type === 'transfer' && Array.isArray((activeStep as any).groupedProgress) && (activeStep as any).groupedProgress.length > 0) {
    return (
      <div
        key={`active-step-${activeStep?.type}`}
        className="step-card single-card current-step"
        data-step-type={activeStep?.type}
        onClick={toggleProcessExpansion}
      >
        <div className={`step-card-content ${isCurrentlyStreaming ? 'streaming' : ''}`}>
          <div className="progress-content">
            {(() => {
              const list: Tag[] = (activeStep as any).groupedProgress;
              const latest = list[list.length - 1];
              const messageToShow = parseProgressContent(latest.content);
              const stepKey = `${getProgressStepId(latest.content)}-latest`;
              return (
                <div key={`compact-progress-${stepKey}`} className="markdown-content mb-2 flex items-center">
                  {isCurrentlyStreaming && thinkingMode === ChatTimelineThinkingMode.OFF && (
                    <div className="thinking-dots mr-1">
                      <div className="thinking-dot"></div>
                      <div className="thinking-dot"></div>
                      <div className="thinking-dot"></div>
                    </div>
                  )}
                  <p className="m-0">{messageToShow}</p>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    );
  }

  if (activeStep?.type === 'transfer') {
    return (
      <div
        key={`active-step-${activeStep?.type}`}
        className="step-card single-card current-step"
        data-step-type={activeStep?.type}
        onClick={toggleProcessExpansion}
      >
        <div className={`step-card-content ${isCurrentlyStreaming ? 'streaming' : ''}`}>
          {/* Intentionally left blank to avoid repeating transfer text */}
        </div>
      </div>
    );
  }

  if (activeStep?.type === 'summarization') {
    return (
      <div
        key={`active-step-${activeStep?.type}`}
        className="step-card single-card current-step"
        data-step-type={activeStep?.type}
        onClick={toggleProcessExpansion}
      >
        <div className={`step-card-content ${isCurrentlyStreaming ? 'streaming' : ''}`}>
          {/* Intentionally left blank to avoid repeating summarization text */}
        </div>
      </div>
    );
  }

  return (
    <div
      key={`active-step-${activeStep?.type}`}
      className="step-card single-card current-step"
      data-step-type={activeStep?.type}
      onClick={toggleProcessExpansion}
    >
      {enhancedPreview && (
        <div className={`step-card-content ${isCurrentlyStreaming ? 'streaming' : ''}`}>
          {enhancedPreview.type === 'structured' ? (
            <div className="research-content">
              {renderMarkdownContent(enhancedPreview.content, false)}
            </div>
          ) : enhancedPreview.type === 'full_content' ? (
            <div className="thinking-content">
              {renderMarkdownContent(enhancedPreview.content, false)}
            </div>
          ) : (
            <div
              className={`${enhancedPreview.type === 'status' && activeStep?.type === 'progress'
                  ? 'progress-content'
                  : 'transfer-content'
                }`}
            >
              <div className="markdown-content">
                <p>{enhancedPreview.title}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ActiveStepCard;